import React from "react";

/**
 * /opiniones — reviews & trust page, rebuilt as a page-scoped Gatsby page
 * (2026-09 redesign, same file-based pattern and visual language as
 * /guia-seguro-vida: DM Sans + Merriweather headline formula, purple
 * var(--primary-color), soft cards, chapter badges, scoped link reset).
 *
 * Self-contained body used only by src/pages/opiniones.tsx; no shared
 * component is modified. Reviews are the real Google reviews the ES home
 * shows in its marquee (same names/dates/text — refresh both together), plus
 * the paid-claim review (CLAIM_REVIEW), first in the grid with its badge.
 * Pricing rule (Carlos, 2026-08-13): no personalised estimates, only base
 * "desde X €/mes". Registry codes per ai-cms/docs/registration-codes.md.
 */

const CALC_URL = "https://app.life5.es/calculator/1";
const PHONE_LABEL = "911 984 986";
const PHONE_HREF = "tel:+34911984986";
const MAIL = "ayuda@life5.com";
const TRUSTPILOT_URL = "https://es.trustpilot.com/review/life5.es";
// Aligned to 4,7 on 2026-09-03 (Carlos) across /opiniones/, the listicle and llms.txt.
const TRUSTPILOT_SCORE = "4,7";
const GOOGLE_REVIEWS_URL = "https://maps.app.goo.gl/8PgcVYRQYTGa2z2N9";
const GOOGLE_SCORE = "4,4";
const GOOGLE_SCORE_NUM = 4.4;
// Month the platform ratings were last read by a person. Bump when refreshed.
const RATINGS_CHECKED = "septiembre de 2026";
const UPDATED_LABEL = "septiembre 2026";

const BUCKET = "https://storage.googleapis.com/getlife-bucket-1/public";
const IMG = {
  hero: `${BUCKET}/Guide/life5-family.webp`,
};

// Palette (mirrors the guide; purple = var(--primary-color))
const INK = "#1F2937";
const INK2 = "#555873";
const INK3 = "#8C90AD";
const LINE = "#E6E8F2";

// ---- Data ----------------------------------------------------------------

// Same curated Google reviews as the ES home `component.reviews` block
// (docId poo6mbtx4ywfy668m4nyq1z4, captured 2026-06-11). All 5★, surnames
// abbreviated. Dates are the absolute month each relative label resolved to
// on capture day, so they never rot (Carlos, 2026-09-03: keep data current).
const GOOGLE_REVIEWS = [
  // Added 2026-10-29 (Reviews.tsx EXTRA_ES_REVIEWS on the home side).
  { name: "Sharon S.", date: "octubre de 2026", text: "Una asesoría impecable, transparente y rápida !!!!" },
  { name: "Hedwin A.", date: "mayo de 2026", text: "Excelente servicio de Catalina Prado! Muy amable, atenta y rápida en su respuesta." },
  { name: "Diana D.", date: "marzo de 2026", text: "Gracias Catalina! Tu profesionalismo y asesoría me ha hecho tomar la mejor elección. Gracias por tu acompañamiento y por la eficiencia en la gestión de tomar la póliza." },
  { name: "Rosana Á.", date: "marzo de 2026", text: "Muy buena atención, rapidez y precios." },
  { name: "Agustín L.", date: "marzo de 2026", text: "Rápidos y con buena atención, todo online." },
  { name: "Juan Carlos", date: "febrero de 2026", text: "Gestión eficaz por la agilidad y sencillez en la contratación de la póliza. Catalina Prado muy eficiente y amable, mis felicitaciones." },
  { name: "Eva P.", date: "enero de 2026", text: "El trato de la asesora, genial, todo explicado, detallado y el precio estupendo." },
  { name: "Alejandro M.", date: "enero de 2026", text: "Excelente asesoramiento y atención." },
  { name: "Elena", date: "diciembre de 2025", text: "Trato excelente y muy profesional por parte de mi asesora Elisa. Explicaciones claras y precisas, puntualidad en las comunicaciones y una gran amabilidad." },
  { name: "Vicenta A.", date: "diciembre de 2025", text: "Raquel encantadora, persona de las que ya no encuentras. Nunca me he sentido tan bien tratada. Excelente." },
  { name: "Marta M.", date: "diciembre de 2025", text: "Fabulosa la atención y gestión de Catalina Prado, muy profesional." },
  { name: "Jose Luis G.", date: "diciembre de 2025", text: "Rapidez y solvencia a la hora de resolver las dudas que puedan surgir." },
  { name: "Pedro M.", date: "diciembre de 2025", text: "Muy amable en todo el contrato, ha sido un placer. Gracias." },
];

// Review from the family of a policyholder who died: the claim was paid in
// full and on time (Google, 2026-10-29). Same text and name as CLAIM_REVIEW in
// the shared Reviews block; first in the grid, green border + badge, and two
// columns wide on desktop (1 + 13 cards fill a 3-column grid exactly).
const CLAIM_REVIEW = {
  name: "Ivi D.",
  date: "octubre de 2026",
  text: "El padre de mis hijos falleció, el trato con nosotros fue estupendo en todo momento, el trato fue muy respetuoso en cada llamada que yo realice como las que ellos me hicieron. Y el pago de la póliza fue el que teníamos contratado y en su tiempo.",
};

// Deterministic avatar colour per name (same idea as the shared Reviews block):
// the same person always gets the same swatch, SSR and client agree.
const AVATAR_COLORS = ["#423EF4", "#018565", "#1F8FCB", "#C8871B", "#171E33", "#A8327D", "#3430E3", "#12835F"];
const colorFor = (name: string) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const TOC = [
  { href: "#resenas", n: "01 · Reseñas", t: "Lo que dicen en Google" },
  { href: "#fiabilidad", n: "02 · Fiabilidad", t: "¿Es fiable Life5?" },
  { href: "#valoran", n: "03 · Lo que valoran", t: "Por qué nos eligen" },
  { href: "#faq", n: "04 · Dudas", t: "Preguntas frecuentes" },
];

// ---- Shared bits ---------------------------------------------------------

const CtaButton = ({
  label,
  href = CALC_URL,
  ghost = false,
  newTab = true,
  className = "",
  id,
}: {
  label: string;
  href?: string;
  ghost?: boolean;
  newTab?: boolean;
  className?: string;
  id?: string;
}) => (
  <a
    id={id}
    href={href}
    {...(newTab ? { target: "_blank", rel: "noopener" } : {})}
    className={`inline-flex items-center justify-center gap-[8px] rounded-full px-[26px] py-[13px] font-DM font-bold text-[15px] no-underline transition-colors ${
      ghost
        ? "bg-white text-purple shadow-[inset_0_0_0_2px_#C4C2FB] hover:bg-[#ECECFE]"
        : "bg-purple text-white hover:bg-[#3430E3]"
    } ${className}`}
  >
    {label}
  </a>
);

const GoogleG = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" className="shrink-0">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.5 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.7 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.6 5.9c4.5-4.1 7-10.2 7-17.6z" />
    <path fill="#FBBC05" d="M10.5 28.7A14.6 14.6 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.9 2.3-8.3 2.3-6.3 0-11.6-4.2-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
  </svg>
);

const TpStars = ({ className = "" }: { className?: string }) => (
  <span aria-hidden="true" className={`text-[#00B67A] tracking-[2px] text-[14px] ${className}`}>
    ★★★★★
  </span>
);

/** Gold stars with a partial fill (4,4 → the last star ~40% filled). */
const GoldStars = ({ value = 5, size = 14 }: { value?: number; size?: number }) => (
  <span className="relative inline-block leading-none" aria-label={`${value.toString().replace(".", ",")} de 5`} role="img" style={{ fontSize: size, letterSpacing: 1 }}>
    <span aria-hidden="true" className="text-[#DADCE0]">★★★★★</span>
    <span aria-hidden="true" className="absolute left-0 top-0 overflow-hidden whitespace-nowrap text-[#FBBC05]" style={{ width: `${(value / 5) * 100}%` }}>
      ★★★★★
    </span>
  </span>
);

const SectionBadge = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-block rounded-full bg-[#ECECFE] px-[14px] py-[5px] font-DM text-[11px] font-extrabold uppercase tracking-[0.1em] text-purple">
    {children}
  </span>
);

const SectionHead = ({
  badge,
  sans,
  serif,
  lead,
  id,
}: {
  badge: string;
  sans: string;
  serif?: string;
  lead?: React.ReactNode;
  id?: string;
}) => (
  <div className="mx-auto mb-[36px] max-w-[66ch] text-center">
    <SectionBadge>{badge}</SectionBadge>
    <h2
      id={id}
      className="mt-[14px] font-DM text-[28px] font-bold leading-[1.18] tracking-[-0.02em] md:text-[34px] lg:text-[38px]"
      style={{ color: INK, textWrap: "balance" as never }}
    >
      {sans}{" "}
      {serif ? <span className="font-MW font-normal italic text-purple">{serif}</span> : null}
    </h2>
    {lead ? (
      <p className="mx-auto mt-[14px] font-MW text-[17px] leading-[1.8]" style={{ color: INK2 }}>
        {lead}
      </p>
    ) : null}
  </div>
);

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div
    className={`rounded-[18px] border bg-white p-[24px] shadow-[0_1px_2px_rgba(31,41,55,.04),0_12px_32px_rgba(31,41,55,.07)] ${className}`}
    style={{ borderColor: LINE }}
  >
    {children}
  </div>
);

const CheckDot = () => (
  <span className="mt-[3px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#E3F5EE]">
    <svg viewBox="0 0 12 12" width="10" height="10" fill="none" aria-hidden="true">
      <path d="M2 6.2 4.8 9 10 3.4" stroke="#018565" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const Pill = ({ children, href }: { children: React.ReactNode; href?: string }) => {
  const cls = "inline-flex items-center gap-[7px] rounded-full border bg-white px-[13px] py-[7px] no-underline";
  return href ? (
    <a href={href} target="_blank" rel="noopener" className={cls} style={{ borderColor: LINE, color: INK2 }}>
      {children}
    </a>
  ) : (
    <span className={cls} style={{ borderColor: LINE, color: INK2 }}>
      {children}
    </span>
  );
};

// ---- Hero ----------------------------------------------------------------

const Hero = () => (
  <section className="bg-[#F5F6FB]" aria-labelledby="opiniones-h1">
    <div className="mx-auto grid max-w-[1140px] items-center gap-[36px] px-[20px] pb-[40px] pt-[36px] md:px-[36px] lg:grid-cols-[1.05fr_0.95fr] lg:pb-[56px]">
      <div>
        <p className="mb-[14px] font-DM text-[11.5px] font-bold uppercase tracking-[0.11em] text-purple">
          Opiniones Life5 · Actualizado {UPDATED_LABEL} · Reseñas públicas y verificables
        </p>
        <h1
          id="opiniones-h1"
          className="max-w-[18ch] font-DM text-[34px] font-extrabold leading-[1.12] tracking-[-0.03em] md:text-[44px] lg:text-[52px]"
          style={{ color: INK, textWrap: "balance" as never }}
        >
          Opiniones de Life5:{" "}
          <span className="font-MW font-normal italic tracking-normal text-purple">
            lo que dicen quienes ya nos han elegido.
          </span>
        </h1>
        <p className="mt-[18px] max-w-[54ch] font-DM text-[17.5px] leading-[1.65]" style={{ color: INK2 }}>
          Reseñas reales de clientes publicadas en Google, los datos que hacen fiable a Life5
          (quién asegura tu póliza y quién nos supervisa) y respuestas directas a las dudas que
          nos hacéis antes de contratar. Todo enlazado a fuentes públicas para que lo compruebes tú.
        </p>
        <div className="mt-[26px] flex flex-wrap gap-[12px]">
          <CtaButton label="Calcular mi precio en 2 min" id="opiniones-hero-cta" />
          <CtaButton label="Leer las reseñas ↓" href="#resenas" ghost newTab={false} />
        </div>
        <ul className="mt-[22px] flex list-none flex-wrap gap-[8px] p-0 font-DM text-[13px]" style={{ color: INK2 }}>
          <li>
            <Pill href={GOOGLE_REVIEWS_URL}>
              <GoogleG /> <strong style={{ color: INK }}>{GOOGLE_SCORE}/5</strong>&nbsp;en Google
            </Pill>
          </li>
          <li>
            <Pill href={TRUSTPILOT_URL}>
              <TpStars className="!text-[12px]" /> <strong style={{ color: INK }}>{TRUSTPILOT_SCORE}/5</strong>&nbsp;en Trustpilot
            </Pill>
          </li>
          <li>
            <Pill>Correduría DGSFP&nbsp;<strong style={{ color: INK }}>J3945</strong></Pill>
          </li>
          <li>
            <Pill>Pólizas aseguradas por&nbsp;<strong style={{ color: INK }}>AXA</strong></Pill>
          </li>
          <li>
            <Pill>Cancela cuando quieras</Pill>
          </li>
        </ul>
      </div>
      <div className="relative">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -inset-[24px] -z-0 rounded-[40px]"
          style={{ background: "radial-gradient(60% 60% at 60% 40%, rgba(66,62,244,.16), transparent 72%)" }}
        />
        <img
          src={IMG.hero}
          alt="Familia protegida con un seguro de vida Life5 disfrutando al aire libre"
          width={1025}
          height={461}
          loading="eager"
          className="relative w-full rounded-[26px] object-cover shadow-[0_6px_18px_rgba(31,41,55,.10),0_28px_60px_rgba(66,62,244,.16)]"
        />
        <div className="absolute bottom-[18px] left-[18px] flex items-center gap-[8px] rounded-[14px] bg-white px-[16px] py-[10px] font-DM text-[13px] font-bold shadow-[0_2px_10px_rgba(31,41,55,.12)]" style={{ color: INK }}>
          <GoldStars value={GOOGLE_SCORE_NUM} size={13} /> +50.000 familias protegidas
        </div>
      </div>
    </div>
  </section>
);

// ---- Índice --------------------------------------------------------------

const Toc = () => (
  <nav aria-label="Índice de la página" className="border-b bg-[#F5F6FB]" style={{ borderColor: LINE }}>
    <div className="mx-auto max-w-[1140px] px-[20px] pb-[30px] md:px-[36px]">
      <p className="mb-[10px] font-DM text-[11px] font-bold uppercase tracking-[0.11em]" style={{ color: INK3 }}>
        4 apartados · ve directo a lo que te interesa
      </p>
      <ul className="op-strip -mx-[20px] flex snap-x list-none gap-[10px] overflow-x-auto px-[20px] pb-[4px] md:-mx-[36px] md:px-[36px] lg:mx-0 lg:grid lg:grid-cols-4 lg:overflow-visible lg:px-0">
        {TOC.map((c) => {
          const [num, label] = c.n.split(" · ");
          return (
            <li key={c.href} className="w-[210px] shrink-0 snap-start lg:w-auto">
              <a
                href={c.href}
                className="op-lift flex h-full items-start gap-[12px] rounded-[14px] border bg-white px-[14px] py-[12px] font-DM text-[12px] no-underline"
                style={{ borderColor: LINE, color: INK3 }}
              >
                <span className="mt-[1px] inline-flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[9px] bg-[#ECECFE] font-DM text-[12px] font-extrabold text-purple tabular-nums">
                  {num}
                </span>
                <span>
                  <b className="block text-[13.5px] leading-[1.3]" style={{ color: INK }}>
                    {label}
                  </b>
                  {c.t}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  </nav>
);

// ---- Banda de datos ------------------------------------------------------

const STATS = [
  { v: `${GOOGLE_SCORE}/5`, t: "valoración media de Life5 en Google Reviews, en un perfil público que no controlamos.", src: `Google · consultado en ${RATINGS_CHECKED}` },
  { v: `${TRUSTPILOT_SCORE}/5`, t: "valoración en Trustpilot, donde respondemos a todas las reseñas, también a las críticas.", src: `Trustpilot · consultado en ${RATINGS_CHECKED}` },
  { v: "+50.000", t: "familias protegidas con un seguro de vida Life5 desde que empezamos en 2021.", src: "Dato Life5" },
  { v: "J3945", t: "clave de Your Life Correduría de Seguros en el registro público de la DGSFP, el regulador español.", src: "Dirección General de Seguros" },
];

const StatsBand = () => (
  <section className="bg-white py-[40px]" aria-label="Cifras de confianza">
    <div className="mx-auto grid max-w-[1140px] grid-cols-1 gap-[16px] px-[20px] sm:grid-cols-2 md:px-[36px] lg:grid-cols-4">
      {STATS.map((s) => (
        <Card key={s.v} className="!p-[22px]">
          <div className="font-DM text-[30px] font-extrabold tracking-[-0.02em] text-purple tabular-nums">{s.v}</div>
          <p className="mt-[2px] font-DM text-[13.5px] leading-[1.5]" style={{ color: INK2 }}>
            {s.t}
          </p>
          <p className="mt-[6px] font-DM text-[11px]" style={{ color: INK3 }}>
            {s.src}
          </p>
        </Card>
      ))}
    </div>
  </section>
);

// ---- 01 · Reseñas de Google ---------------------------------------------

const ClaimPaidBadge = () => (
  <span className="inline-flex items-center gap-[6px] rounded-full bg-[#E6F4EA] px-[11px] py-[4px] font-DM text-[12px] font-bold text-[#137333]">
    <span aria-hidden="true">✓</span> Indemnización pagada
  </span>
);

const ReviewCard = ({ r }: { r: (typeof GOOGLE_REVIEWS)[number] }) => (
  <li
    className={`flex flex-col rounded-[18px] bg-white p-[20px] shadow-[0_1px_2px_rgba(31,41,55,.04),0_12px_32px_rgba(31,41,55,.07)] ${r === CLAIM_REVIEW ? "border-2 lg:col-span-2" : "border"}`}
    style={{ borderColor: r === CLAIM_REVIEW ? "#34A853" : LINE }}
  >
    <div className="flex items-center gap-[10px]">
      <span className="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full font-DM text-[14px] font-bold text-white" style={{ background: colorFor(r.name) }} aria-hidden="true">
        {r.name[0]}
      </span>
      <span className="min-w-0">
        <b className="block truncate font-DM text-[14px]" style={{ color: INK }}>
          {r.name}
        </b>
        <span className="font-DM text-[11.5px]" style={{ color: INK3 }}>
          {r.date}
        </span>
      </span>
      <span className="ml-auto shrink-0">
        <GoogleG size={16} />
      </span>
    </div>
    <div className="mt-[10px]">
      <GoldStars size={13} />
    </div>
    {r === CLAIM_REVIEW ? (
      <div className="mt-[8px]">
        <ClaimPaidBadge />
      </div>
    ) : null}
    <p className="mb-0 mt-[6px] font-DM text-[14px] leading-[1.6]" style={{ color: INK2 }}>
      “{r.text}”
    </p>
  </li>
);

const SectionResenas = () => (
  <section id="resenas" className="scroll-mt-[90px] bg-[#F5F6FB] py-[56px] lg:py-[80px]">
    <div className="mx-auto max-w-[1140px] px-[20px] md:px-[36px]">
      <SectionHead
        badge="01 · Reseñas"
        sans="Opiniones reales de clientes."
        serif="Esto dicen en Google."
        lead={
          <>
            Una selección de reseñas recientes publicadas en el perfil público de Life5 en Google,
            tal cual las escribieron sus autores. Las puedes leer todas, incluidas las críticas,
            en el propio perfil.
          </>
        }
      />

      <div className="mx-auto mb-[28px] flex max-w-[760px] flex-col items-center gap-[14px] rounded-[20px] border bg-white p-[22px] text-center shadow-[0_1px_2px_rgba(31,41,55,.04),0_12px_32px_rgba(31,41,55,.07)] sm:flex-row sm:justify-between sm:text-left" style={{ borderColor: LINE }}>
        <div className="flex items-center gap-[14px]">
          <span className="font-DM text-[44px] font-extrabold leading-none tracking-[-0.02em] tabular-nums" style={{ color: INK }}>
            {GOOGLE_SCORE}
          </span>
          <span className="flex flex-col">
            <GoldStars value={GOOGLE_SCORE_NUM} size={18} />
            <span className="mt-[6px] inline-flex items-center gap-[6px] font-DM text-[13px] font-semibold" style={{ color: INK2 }}>
              <GoogleG /> Google Reviews
            </span>
          </span>
        </div>
        <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener" className="inline-flex items-center gap-[4px] whitespace-nowrap rounded-full bg-[#ECECFE] px-[18px] py-[10px] font-DM text-[13.5px] font-bold text-purple no-underline hover:bg-[#E0DFFC]">
          Ver todas en Google →
        </a>
      </div>

      <ul className="m-0 grid list-none gap-[14px] p-0 md:grid-cols-2 lg:grid-cols-3">
        {[CLAIM_REVIEW, ...GOOGLE_REVIEWS].map((r) => (
          <ReviewCard key={`${r.name}-${r.date}`} r={r} />
        ))}
      </ul>

      <h3 id="valoraciones-life5-internet" className="mb-[16px] mt-[44px] text-center font-DM text-[22px] font-bold" style={{ color: INK }}>
        Las valoraciones de Life5 en internet
      </h3>
      <p className="mx-auto mb-[18px] max-w-[66ch] text-center font-DM text-[15px] leading-[1.65]" style={{ color: INK2 }}>
        Las reseñas públicas de Life5 están en plataformas independientes que no controlamos: allí
        puedes leer tanto las opiniones positivas como las negativas, y cómo respondemos a cada una.
      </p>
      <div className="mx-auto max-w-[760px] overflow-x-auto">
        <table className="w-full border-separate border-spacing-0 overflow-hidden rounded-[18px] border bg-white font-DM text-[14px] shadow-[0_1px_2px_rgba(31,41,55,.04),0_12px_32px_rgba(31,41,55,.07)]" style={{ borderColor: LINE }}>
          <thead>
            <tr>
              {["Plataforma", "Valoración", "Última consulta"].map((h) => (
                <th key={h} className="border-b bg-[#EFEEFC] px-[18px] py-[14px] text-left text-[13px] font-bold text-purple" style={{ borderColor: LINE }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border-b px-[18px] py-[14px] font-semibold" style={{ borderColor: LINE, color: INK }}>
                <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener" className="inline-flex items-center gap-[7px] font-semibold no-underline" style={{ color: INK }}>
                  <GoogleG /> Google
                </a>
              </td>
              <td className="border-b px-[18px] py-[14px] tabular-nums" style={{ borderColor: LINE, color: INK }}>
                <b>{GOOGLE_SCORE}/5</b>
              </td>
              <td className="border-b px-[18px] py-[14px]" style={{ borderColor: LINE, color: INK2 }}>
                {RATINGS_CHECKED}
              </td>
            </tr>
            <tr>
              <td className="px-[18px] py-[14px] font-semibold" style={{ color: INK }}>
                <a href={TRUSTPILOT_URL} target="_blank" rel="noopener" className="inline-flex items-center gap-[7px] font-semibold no-underline" style={{ color: INK }}>
                  <TpStars className="!text-[11px]" /> Trustpilot
                </a>
              </td>
              <td className="px-[18px] py-[14px] tabular-nums" style={{ color: INK }}>
                <b>{TRUSTPILOT_SCORE}/5</b>
              </td>
              <td className="px-[18px] py-[14px]" style={{ color: INK2 }}>
                {RATINGS_CHECKED}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mx-auto mt-[16px] max-w-[70ch] text-center font-DM text-[13px]" style={{ color: INK3 }}>
        Si ya eres cliente, deja tu opinión en Google: nos ayuda a mejorar el producto y el servicio.
      </p>
    </div>
  </section>
);

// ---- 02 · Fiabilidad -----------------------------------------------------

const TRUST_CARDS = [
  {
    tag: "Quién nos supervisa",
    t: "Correduría registrada en la DGSFP",
    d: (
      <>
        Life5 es la marca comercial de <strong>Your Life Correduría de Seguros S.L.</strong>, inscrita en el
        registro público de la Dirección General de Seguros y Fondos de Pensiones con la clave{" "}
        <strong>J3945</strong>. Puedes comprobarla en el buscador de la DGSFP.
      </>
    ),
  },
  {
    tag: "Quién asegura tu póliza",
    t: "El riesgo lo soporta AXA",
    d: (
      <>
        Las pólizas que distribuimos están aseguradas por <strong>AXA</strong>, una de las mayores aseguradoras
        del mundo. Life5 diseña, asesora y gestiona; el compromiso de pago de la indemnización es de AXA.{" "}
        <a href="/seguros-axa/">Así funciona la relación con AXA</a>.
      </>
    ),
  },
  {
    tag: "Quién está detrás",
    t: "Un equipo real en Madrid desde 2021",
    d: (
      <>
        Nacimos en 2021 como Getlife y hoy operamos en España, Italia y Francia. Detrás de la web hay
        asesores con nombre y apellidos.{" "}
        <a href="/nosotros-equipo/">Conoce al equipo</a> o mira{" "}
        <a href="/prensa/">lo que han publicado los medios</a>.
      </>
    ),
  },
];

const HELP_STEPS = [
  { k: "Primero", t: "Escríbenos o llámanos", d: <>Desde <a href="/contacto/">la página de contacto</a> o en el <a href={PHONE_HREF}>{PHONE_LABEL}</a>. Casi todo se resuelve en esa primera conversación.</> },
  { k: "Si no se resuelve", t: "Abre una queja formal", d: <>Tienes a tu disposición el <a href="/quejas-reclamaciones/">canal de quejas y reclamaciones</a>, con los plazos y el procedimiento que marca la normativa.</> },
  { k: "Siempre", t: "Respondemos en público", d: <>Contestamos a todas las reseñas en Google y Trustpilot y usamos cada crítica para revisar el proceso o la gestión que falló.</> },
];

const SectionFiabilidad = () => (
  <section id="fiabilidad" className="scroll-mt-[90px] bg-white py-[56px] lg:py-[80px]">
    <div className="mx-auto max-w-[1140px] px-[20px] md:px-[36px]">
      <SectionHead
        badge="02 · Fiabilidad"
        sans="¿Es fiable Life5?"
        serif="Sí, y puedes comprobarlo."
        lead={
          <>
            Dos cosas prácticas: la actividad de Life5 está supervisada por el regulador español de
            seguros, y el riesgo de tu póliza no lo soporta una startup, sino AXA. Ambos datos están
            en registros públicos.
          </>
        }
      />

      <div className="grid gap-[16px] md:grid-cols-3">
        {TRUST_CARDS.map((c) => (
          <Card key={c.t}>
            <span className="mb-[10px] inline-block rounded-full bg-[#ECECFE] px-[10px] py-[3px] font-DM text-[12px] font-bold text-purple">
              {c.tag}
            </span>
            <h3 className="font-DM text-[18px] font-bold" style={{ color: INK }}>
              {c.t}
            </h3>
            <p className="mb-0 mt-[6px] font-DM text-[14px] leading-[1.6]" style={{ color: INK2 }}>
              {c.d}
            </p>
          </Card>
        ))}
      </div>

      <div className="mx-auto mt-[44px] grid max-w-[1140px] gap-[24px] rounded-[20px] border bg-[#F5F6FB] p-[24px] md:p-[32px] lg:grid-cols-[1fr_1.1fr]" style={{ borderColor: LINE }}>
        <div>
          <h3 id="opiniones-negativas" className="font-DM text-[22px] font-bold" style={{ color: INK }}>
            ¿Y las opiniones negativas de Life5?
          </h3>
          <p className="mt-[8px] font-DM text-[15px] leading-[1.65]" style={{ color: INK2 }}>
            Existen, como en cualquier compañía con miles de clientes, y son públicas: no las filtramos
            ni las borramos. Lo que sí hacemos es responder a cada una y, si tienes un problema con tu
            póliza, darte un camino claro para resolverlo.
          </p>
          <p className="mb-0 mt-[10px] font-DM text-[15px] leading-[1.65]" style={{ color: INK2 }}>
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener">Léelas en Google</a> o{" "}
            <a href={TRUSTPILOT_URL} target="_blank" rel="noopener">en Trustpilot</a>, junto con nuestras respuestas.
          </p>
        </div>
        <ol className="relative m-0 list-none p-0 pl-[34px]" style={{ color: INK }}>
          <span aria-hidden="true" className="absolute bottom-[8px] left-[10px] top-[8px] w-[2px] bg-[#D5D7E8]" />
          {HELP_STEPS.map((s) => (
            <li key={s.t} className="relative pb-[20px] last:pb-0">
              <span aria-hidden="true" className="absolute left-[-31px] top-[5px] h-[16px] w-[16px] rounded-full border-4 border-[#F5F6FB] bg-purple shadow-[0_0_0_2px_var(--primary-color)]" />
              <span className="font-DM text-[11.5px] font-bold uppercase tracking-[0.06em] text-purple">{s.k}</span>
              <b className="block font-DM text-[16px]">{s.t}</b>
              <p className="mb-0 mt-[2px] font-DM text-[14px] leading-[1.6]" style={{ color: INK2 }}>
                {s.d}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  </section>
);

// ---- 03 · Lo que más valoran ---------------------------------------------

const VALUE_CARDS = [
  {
    tag: "Trato",
    t: "Asesoramiento humano y cercano",
    d: "Un asesor con nombre y apellidos te acompaña por teléfono antes de firmar, sin coste y sin presión. Es lo que más se repite en las reseñas: trato amable y explicaciones claras.",
    quote: { text: "Explicaciones claras y precisas, puntualidad en las comunicaciones y una gran amabilidad.", who: "Elena, en Google" },
  },
  {
    tag: "Rapidez",
    t: "Contratación 100% online en minutos",
    d: "Calcula tu precio, responde el cuestionario de salud digital y firma desde el móvil. Sin citas, sin papeleo y sin reconocimiento médico.",
    quote: { text: "Rápidos y con buena atención, todo online.", who: "Agustín L., en Google" },
  },
  {
    tag: "Precio",
    t: "Precio claro desde el primer minuto",
    d: "El precio que ves en la calculadora es el que pagas: sin recargos por pagar mes a mes y sin permanencia. Puedes cancelar cuando quieras.",
    quote: { text: "Todo explicado, detallado y el precio estupendo.", who: "Eva P., en Google" },
  },
  {
    tag: "Respaldo",
    t: "AXA detrás de cada póliza",
    d: "Life5 diseña, asesora y gestiona tu seguro; AXA asegura el riesgo. Una correduría registrada en la DGSFP y una aseguradora global en cada contrato.",
    quote: { text: "Clave J3945 en el registro público de la DGSFP.", who: "Verificable en la Dirección General de Seguros" },
  },
];

const SectionValoran = () => (
  <section id="valoran" className="scroll-mt-[90px] bg-[#F5F6FB] py-[56px] lg:py-[80px]">
    <div className="mx-auto max-w-[1140px] px-[20px] md:px-[36px]">
      <SectionHead
        badge="03 · Lo que valoran"
        sans="Lo que más valoran"
        serif="quienes ya tienen Life5."
        lead={<>Cuatro cosas que se repiten en las opiniones de nuestros clientes, con sus propias palabras.</>}
      />
      <div className="grid gap-[16px] md:grid-cols-2">
        {VALUE_CARDS.map((c) => (
          <Card key={c.t} className="flex flex-col">
            <span className="mb-[10px] inline-block self-start rounded-full bg-[#E3F5EE] px-[10px] py-[3px] font-DM text-[12.5px] font-bold text-[#018565]">
              {c.tag}
            </span>
            <h3 className="font-DM text-[19px] font-bold" style={{ color: INK }}>
              {c.t}
            </h3>
            <p className="mt-[6px] font-DM text-[14px] leading-[1.6]" style={{ color: INK2 }}>
              {c.d}
            </p>
            <blockquote className="mt-auto flex gap-[8px] border-t pt-[14px] font-DM text-[13.5px] leading-[1.55]" style={{ borderColor: LINE, color: INK2 }}>
              <CheckDot />
              <span>
                “{c.quote.text}” <span style={{ color: INK3 }}>— {c.quote.who}</span>
              </span>
            </blockquote>
          </Card>
        ))}
      </div>
      <p className="mx-auto mt-[22px] max-w-[70ch] text-center font-DM text-[13px]" style={{ color: INK3 }}>
        ¿Quieres saber qué cubre exactamente? Mira las{" "}
        <a href="/coberturas-seguro-vida/">coberturas del seguro de vida Life5</a> o lee la{" "}
        <a href="/guia-seguro-vida/">guía del seguro de vida</a> antes de{" "}
        <a href="/calcular-seguro-vida/">calcular tu precio</a>.
      </p>
    </div>
  </section>
);

// ---- 04 · FAQ ------------------------------------------------------------

export const FAQ_ITEMS: Array<{ q: string; a: React.ReactNode; plain: string }> = [
  {
    q: "¿Life5 es fiable y seguro?",
    a: <>Sí: Life5 opera a través de Your Life Correduría de Seguros S.L., inscrita en la DGSFP con clave J3945, y sus pólizas están aseguradas por AXA. Ambos datos son verificables en registros públicos.</>,
    plain: "Sí: Life5 opera a través de Your Life Correduría de Seguros S.L., inscrita en la DGSFP con clave J3945, y sus pólizas están aseguradas por AXA. Ambos datos son verificables en registros públicos.",
  },
  {
    q: "¿Quién asegura realmente mi póliza con Life5?",
    a: <>La aseguradora del riesgo es AXA. Life5 actúa como correduría: diseña la experiencia, te asesora y gestiona tu póliza, pero el compromiso de pago de la indemnización lo asume AXA.</>,
    plain: "La aseguradora del riesgo es AXA. Life5 actúa como correduría: diseña la experiencia, te asesora y gestiona tu póliza, pero el compromiso de pago de la indemnización lo asume AXA.",
  },
  {
    q: "¿Life5 es de AXA?",
    a: <>No. Life5 es una correduría de seguros independiente (Your Life Correduría de Seguros S.L.) y no forma parte del grupo AXA. AXA es la aseguradora que emite y respalda las pólizas de vida que Life5 distribuye en life5.es.</>,
    plain: "No. Life5 es una correduría de seguros independiente (Your Life Correduría de Seguros S.L.) y no forma parte del grupo AXA. AXA es la aseguradora que emite y respalda las pólizas de vida que Life5 distribuye en life5.es.",
  },
  {
    q: "¿Qué pasa con mi seguro si Life5 desapareciera?",
    a: <>Tu contrato de seguro es con la aseguradora, no con la correduría, así que tu póliza seguiría en vigor con AXA en sus mismos términos. La correduría es el intermediario, no quien soporta el riesgo.</>,
    plain: "Tu contrato de seguro es con la aseguradora, no con la correduría, así que tu póliza seguiría en vigor con AXA en sus mismos términos. La correduría es el intermediario, no quien soporta el riesgo.",
  },
  {
    q: "¿Life5 paga cuando fallece el asegurado?",
    a: <>Sí. La indemnización la paga la aseguradora por el capital contratado, y Life5 acompaña a la familia y gestiona el trámite. En nuestro perfil de Google puedes leer la reseña de una familia que cobró: «el pago de la póliza fue el que teníamos contratado y en su tiempo».</>,
    plain: "Sí. La indemnización la paga la aseguradora por el capital contratado, y Life5 acompaña a la familia y gestiona el trámite. En nuestro perfil de Google puedes leer la reseña de una familia que cobró: «el pago de la póliza fue el que teníamos contratado y en su tiempo».",
  },
  {
    q: "¿Dónde puedo leer opiniones reales de clientes de Life5?",
    a: <>En los perfiles públicos de Life5 en <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener">Google</a> y <a href={TRUSTPILOT_URL} target="_blank" rel="noopener">Trustpilot</a>. No filtramos ni editamos esas reseñas; en esta página mostramos una selección de las publicadas en Google.</>,
    plain: "En los perfiles públicos de Life5 en Google y Trustpilot. No filtramos ni editamos esas reseñas; en esta página mostramos una selección de las publicadas en Google.",
  },
  {
    q: "¿Life5 tiene opiniones negativas?",
    a: <>Sí, y son públicas. Respondemos a todas en Google y Trustpilot y revisamos cada caso. Si tienes un problema con tu póliza, escríbenos desde <a href="/contacto/">contacto</a> o llama al <a href={PHONE_HREF}>{PHONE_LABEL}</a>; para una queja formal, usa el <a href="/quejas-reclamaciones/">canal de quejas y reclamaciones</a>.</>,
    plain: "Sí, y son públicas. Respondemos a todas en Google y Trustpilot y revisamos cada caso. Si tienes un problema con tu póliza, escríbenos desde la página de contacto o llama al 911 984 986; para una queja formal, usa el canal de quejas y reclamaciones.",
  },
  {
    q: "¿Life5 y Getlife son la misma empresa?",
    a: <>Sí. La compañía se fundó en 2021 como Getlife y pasó a llamarse Life5. La entidad legal (Your Life Correduría de Seguros S.L., clave DGSFP J3945) es la misma.</>,
    plain: "Sí. La compañía se fundó en 2021 como Getlife y pasó a llamarse Life5. La entidad legal (Your Life Correduría de Seguros S.L., clave DGSFP J3945) es la misma.",
  },
  {
    q: "¿Cuánto se tarda en contratar y cómo me atienden?",
    a: <>La contratación es 100% online y se completa en minutos, con cuestionario de salud digital. Si lo prefieres, un asesor te acompaña por teléfono antes de firmar, sin coste y sin compromiso.</>,
    plain: "La contratación es 100% online y se completa en minutos, con cuestionario de salud digital. Si lo prefieres, un asesor te acompaña por teléfono antes de firmar, sin coste y sin compromiso.",
  },
];

const SectionFaq = () => (
  <section id="faq" className="scroll-mt-[90px] bg-white py-[56px] lg:py-[80px]">
    <div className="mx-auto max-w-[1140px] px-[20px] md:px-[36px]">
      <SectionHead
        badge="04 · Dudas"
        sans="Las preguntas"
        serif="que nos hacéis antes de contratar."
        lead={
          <>
            Respuestas directas sobre la fiabilidad de Life5. Si tu duda no está aquí, llámanos al{" "}
            <a href={PHONE_HREF}>{PHONE_LABEL}</a> o escribe a <a href={`mailto:${MAIL}`}>{MAIL}</a>.
          </>
        }
      />
      <div className="mx-auto max-w-[760px]">
        {FAQ_ITEMS.map((f) => (
          <details key={f.q} className="op-faq mb-[10px] overflow-hidden rounded-[14px] border bg-white shadow-[0_2px_10px_rgba(31,41,55,.06)]" style={{ borderColor: LINE }}>
            <summary className="flex cursor-pointer list-none items-center justify-between gap-[14px] px-[20px] py-[17px] font-DM text-[15.5px] font-bold" style={{ color: INK }}>
              {f.q}
              <span aria-hidden="true" className="op-faq-plus shrink-0 font-DM text-[21px] font-normal leading-none text-purple transition-transform">
                +
              </span>
            </summary>
            <p className="m-0 px-[20px] pb-[18px] font-DM text-[14.5px] leading-[1.65]" style={{ color: INK2 }}>
              {f.a}
            </p>
          </details>
        ))}
      </div>
      <div className="mt-[24px] text-center">
        <CtaButton label="Calcular mi precio" />
      </div>
    </div>
  </section>
);

// ---- CTA final -----------------------------------------------------------

const FinalCta = () => (
  <section className="bg-white px-[20px] pb-[64px] pt-[8px] md:px-[36px]">
    <div className="mx-auto max-w-[1140px] rounded-[24px] p-[36px] text-center md:p-[56px]" style={{ background: "linear-gradient(135deg, var(--primary-color), #3430E3)" }}>
      <h2 className="font-DM text-[28px] font-bold tracking-[-0.02em] text-white md:text-[34px]" style={{ textWrap: "balance" as never }}>
        Únete a las familias que ya confían en Life5. <span className="font-MW font-normal italic">Cuesta 2 minutos.</span>
      </h2>
      <p className="mx-auto mb-[22px] mt-[10px] max-w-[48ch] font-DM text-[15.5px] text-white/85">
        Calcula tu precio con tu edad y tu capital. 100% online, sin compromiso y con una persona
        al otro lado del teléfono si la necesitas.
      </p>
      <a href={CALC_URL} target="_blank" rel="noopener" id="opiniones-bottom-cta" className="inline-flex items-center justify-center rounded-full bg-white px-[26px] py-[13px] font-DM text-[15px] font-bold text-[#3430E3] no-underline transition-colors hover:bg-[#EDEDFF]">
        Calcular mi precio ahora
      </a>
      <p className="mb-0 mt-[16px] flex flex-wrap items-center justify-center gap-x-[18px] gap-y-[6px] font-DM text-[13px] text-white/85">
        <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener" className="inline-flex items-center gap-[6px] text-white no-underline">
          <span className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-white"><GoogleG /></span> <b>{GOOGLE_SCORE}/5</b>&nbsp;Google
        </a>
        <a href={TRUSTPILOT_URL} target="_blank" rel="noopener" className="inline-flex items-center gap-[6px] text-white no-underline">
          <span aria-hidden="true" className="text-[#7CE0B8] tracking-[1px]">★★★★★</span> <b>{TRUSTPILOT_SCORE}/5</b>&nbsp;Trustpilot
        </a>
      </p>
      <p className="mb-0 mt-[14px] font-DM text-[13.5px] text-white/80">
        ¿Prefieres hablarlo con una persona?{" "}
        <a href={PHONE_HREF} className="font-bold text-white">
          {PHONE_LABEL}
        </a>{" "}
        ·{" "}
        <a href={`mailto:${MAIL}`} className="font-bold text-white">
          {MAIL}
        </a>
      </p>
    </div>
  </section>
);

// ---- Body ----------------------------------------------------------------

const Opiniones = () => (
  <div className="op-body" style={{ scrollBehavior: "smooth" }}>
    {/*
      Scoped overrides (no shared CSS touched) — same rationale as the guide:
      global.css gives every <a> the text-link-* scale and markdown.css skins
      every blockquote. `:where(.op-body) a` keeps (0,0,1) specificity so it
      beats those base rules by source order while Tailwind utilities (0,1,0)
      on buttons/pills still win. Unclassed anchors are the inline text links.
    */}
    <style>{`
      :where(.op-body) a { color: inherit; font-size: inherit; font-weight: inherit; line-height: inherit; text-decoration: none; }
      .op-body a:not([class]) { color: var(--primary-color); font-weight: 600; text-decoration: underline; text-decoration-color: #C4C2FB; text-decoration-thickness: 1.5px; text-underline-offset: 3px; transition: color .15s ease, text-decoration-color .15s ease; }
      .op-body a:not([class]):hover { color: #3430E3; text-decoration-color: currentColor; }
      .op-body blockquote { background: none; border-left: 0; border-right: 0; border-bottom: 0; margin: 0; padding-left: 0; padding-right: 0; padding-bottom: 0; quotes: none; }
      .op-body blockquote::before, .op-body blockquote::after, .op-body blockquote p::before, .op-body blockquote p::after { content: none; }
      .op-body .op-faq summary::-webkit-details-marker { display: none; }
      .op-body .op-faq[open] .op-faq-plus { transform: rotate(45deg); }
      .op-body .op-strip { scrollbar-width: none; -ms-overflow-style: none; }
      .op-body .op-strip::-webkit-scrollbar { display: none; }
      .op-body .op-lift { transition: transform .18s ease, box-shadow .18s ease; }
      .op-body .op-lift:hover { transform: translateY(-2px); box-shadow: 0 2px 6px rgba(31,41,55,.05), 0 18px 40px rgba(66,62,244,.12); }
      @media (prefers-reduced-motion: reduce) { .op-body .op-lift, .op-body .op-faq-plus { transition: none; } .op-body .op-lift:hover { transform: none; } }
    `}</style>
    <Hero />
    <Toc />
    <StatsBand />
    <SectionResenas />
    <SectionFiabilidad />
    <SectionValoran />
    <SectionFaq />
    <FinalCta />
  </div>
);

export default Opiniones;
